import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Edit2, Plus, Search, ShieldCheck, ShieldOff, Shuffle, Trash2, Users } from 'lucide-react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { PhoneField } from '@/components/PhoneField';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { Modal } from '@/components/Modal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { StatusPill } from '@/components/StatusPill';
import { LoadErrorState } from '@/components/ListStates';
import { SettingsCard, SettingsPage, SettingsSection, SettingsStat, SettingsToggle } from '@/components/settings/SettingsPage';
import { useAuth } from '@/hooks/useAuth';
import { settingsService, TeamUser } from '@/services/settings.service';
import { authService } from '@/services/auth.service';

const initialsOf = (u: TeamUser) => `${u.firstName.charAt(0)}${u.lastName.charAt(0)}`.toUpperCase();
const EMPTY_FORM = { firstName: '', lastName: '', email: '', password: '', role: 'company_user' as 'company_admin' | 'company_user' };

export default function TeamSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'company_admin';
  const [team, setTeam] = useState<TeamUser[] | null>(null);
  const [teamError, setTeamError] = useState(false);
  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<TeamUser | null>(null);
  const [del, setDel] = useState<TeamUser | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [autoAssign, setAutoAssign] = useState<boolean | null>(null);
  const [savingRouting, setSavingRouting] = useState(false);

  const refresh = () => {
    setTeamError(false);
    return settingsService.listTeam().then(setTeam).catch(() => setTeamError(true));
  };

  useEffect(() => {
    refresh();
    settingsService.getLeadRouting().then((r) => setAutoAssign(r.leadAutoAssignEnabled)).catch(() => setAutoAssign(false));
  }, []);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!team) return [];
    return q ? team.filter((u) => `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(q)) : team;
  }, [team, query]);

  const toggleAutoAssign = async (next: boolean) => {
    setSavingRouting(true);
    const prev = autoAssign;
    setAutoAssign(next);
    try {
      const r = await settingsService.setLeadRouting({ leadAutoAssignEnabled: next });
      setAutoAssign(r.leadAutoAssignEnabled);
      toast.success(next ? 'Auto-assign enabled' : 'Auto-assign disabled');
    } catch (e) {
      setAutoAssign(prev);
      if (!(e as { response?: unknown })?.response) toast.error('Failed to save');
    } finally { setSavingRouting(false); }
  };

  const submitCreate = async () => {
    setBusy(true);
    try {
      await settingsService.createTeamUser(form);
      toast.success('User created');
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      refresh();
    } finally { setBusy(false); }
  };

  const submitEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await settingsService.updateTeamUser(editing.userUid, {
        firstName: editing.firstName, lastName: editing.lastName, phone: editing.phone,
        role: editing.role as any, status: editing.status as any,
      });
      toast.success('Saved');
      setEditing(null);
      refresh();
    } finally { setBusy(false); }
  };

  const resetTwoFactor = async (u: TeamUser) => {
    if (!window.confirm(`Turn off two-step verification for ${u.firstName}? They can sign in with their password and set it up again.`)) return;
    try { await authService.resetTeammateTwoFactor(u.userUid); toast.success('Two-step verification reset'); refresh(); }
    catch { /* the axios interceptor already toasted */ }
  };

  const admins = team?.filter((u) => u.role === 'company_admin').length ?? 0;
  const active = team?.filter((u) => u.status === 'active').length ?? 0;
  const twoStep = team?.filter((u) => u.twoFactor?.enabled).length ?? 0;

  return (
    <SettingsPage
      eyebrow="Account"
      title="Team"
      description="Everyone who signs in to this workspace. Admins manage settings and billing; users work leads, contacts and campaigns."
      actions={isAdmin && <Button iconLeft={<Plus size={14} />} onClick={() => setCreateOpen(true)}>Add teammate</Button>}
    >
      <div className="grid grid-cols-2 gap-3 py-6 @2xl:grid-cols-4">
        <SettingsStat label="Members" value={team ? team.length : '—'} />
        <SettingsStat label="Admins" value={team ? admins : '—'} tone="gold" />
        <SettingsStat label="Active" value={team ? active : '—'} tone="success" />
        <SettingsStat label="Two-step on" value={team ? twoStep : '—'} hint={team && twoStep < team.length ? `${team.length - twoStep} without` : undefined} />
      </div>

      <SettingsSection
        title="Members"
        description={isAdmin
          ? 'Edit a teammate\'s name, phone, role or status. Removing someone signs them out immediately.'
          : 'You have view-only access. Ask a company admin to make changes.'}
      >
        <SettingsCard padded={false}>
          <div className="flex items-center gap-3 border-b border-border px-5 py-3">
            <Search size={15} className="shrink-0 text-text-muted" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or email"
              className="h-8 w-full bg-transparent text-sm text-text-primary placeholder:text-text-muted focus:outline-none"
              aria-label="Search team"
            />
            {team && <span className="shrink-0 text-2xs text-text-muted">{shown.length} of {team.length}</span>}
          </div>

          {team === null && teamError ? (
            <LoadErrorState bare what="your team members" onRetry={() => void refresh()} />
          ) : team === null ? (
            <div className="space-y-2 p-5">{[0, 1, 2].map((i) => <div key={i} className="skeleton h-14 rounded-xl" />)}</div>
          ) : shown.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white shadow-soft"><Users size={20} /></div>
              <div className="text-sm font-medium text-text-primary">{query ? 'No one matches that search' : 'No teammates yet'}</div>
              {!query && isAdmin && <Button size="sm" variant="secondary" iconLeft={<Plus size={13} />} onClick={() => setCreateOpen(true)}>Add the first teammate</Button>}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {shown.map((u) => {
                const me = u.userUid === user?.userUid;
                return (
                  <li key={u.userUid} className="group flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-surface-alt/60">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gold-gradient-soft text-sm font-bold text-primary">{initialsOf(u)}</div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="truncate text-sm font-semibold text-text-primary">{u.firstName} {u.lastName}</span>
                        {me && <span className="text-2xs text-text-muted">(you)</span>}
                        <StatusPill status={u.role === 'company_admin' ? 'admin' : 'user'} variant={u.role === 'company_admin' ? 'info' : 'neutral'} />
                        {u.status !== 'active' && <StatusPill status={u.status} />}
                        {u.twoFactor?.enabled && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-success-soft px-1.5 py-0.5 text-2xs font-medium text-success"><ShieldCheck size={11} /> 2-step</span>
                        )}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-text-muted">{u.email}</div>
                    </div>
                    <div className="hidden shrink-0 text-right md:block">
                      <div className="text-2xs uppercase tracking-wide text-text-muted">Last login</div>
                      <div className="text-xs text-text-secondary">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'Never'}</div>
                    </div>
                    {isAdmin && (
                      <div className="flex shrink-0 items-center gap-0.5 md:opacity-0 md:transition-opacity md:group-hover:opacity-100 md:group-focus-within:opacity-100">
                        <button onClick={() => setEditing({ ...u })} className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary" aria-label={`Edit ${u.firstName}`}><Edit2 size={14} /></button>
                        {u.twoFactor?.enabled && !me && (
                          <button onClick={() => resetTwoFactor(u)} className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-warning-soft hover:text-warning" aria-label={`Reset two-step verification for ${u.firstName}`} title="Reset two-step verification"><ShieldOff size={14} /></button>
                        )}
                        {!me && (
                          <button onClick={() => setDel(u)} className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-danger-soft hover:text-danger" aria-label={`Remove ${u.firstName}`}><Trash2 size={14} /></button>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </SettingsCard>
      </SettingsSection>

      <SettingsSection
        title="Lead routing"
        description="How a brand-new lead gets an owner. Department routing still applies: a lead that lands in a department only goes to that department's members."
      >
        <SettingsCard padded={false}>
          <SettingsToggle
            icon={<Shuffle size={16} />}
            label="Auto-assign new leads (round-robin)"
            description="Every new lead — WhatsApp, ads, forms or imports — goes to the active teammate with the fewest open leads."
            checked={!!autoAssign}
            disabled={!isAdmin || autoAssign === null || savingRouting}
            onChange={toggleAutoAssign}
            badge={autoAssign ? <span className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2 py-0.5 text-2xs font-medium text-success"><span className="h-1.5 w-1.5 rounded-full bg-success" /> Active</span> : undefined}
          />
        </SettingsCard>
      </SettingsSection>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Add teammate">
        <div className="grid grid-cols-2 gap-3">
          <FormField label="First name" required><Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></FormField>
          <FormField label="Last name" required><Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} /></FormField>
        </div>
        <FormField label="Email" required><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></FormField>
        <FormField label="Initial password" required hint="Minimum 8 characters">
          <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} invalid={form.password.length > 0 && form.password.length < 8} />
        </FormField>
        <FormField label="Role">
          <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as any })}>
            <option value="company_user">User</option>
            <option value="company_admin">Admin</option>
          </Select>
        </FormField>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button loading={busy} disabled={!form.firstName || !form.email || form.password.length < 8} onClick={submitCreate}>Create user</Button>
        </div>
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Edit ${editing?.firstName ?? ''}`}>
        {editing && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="First name"><Input value={editing.firstName} onChange={(e) => setEditing({ ...editing, firstName: e.target.value })} /></FormField>
              <FormField label="Last name"><Input value={editing.lastName} onChange={(e) => setEditing({ ...editing, lastName: e.target.value })} /></FormField>
            </div>
            <FormField label="Phone"><PhoneField value={editing.phone ?? ''} onChange={(e164) => setEditing({ ...editing, phone: e164 })} /></FormField>
            {isAdmin && (
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Role">
                  <Select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value as any })}>
                    <option value="company_user">User</option>
                    <option value="company_admin">Admin</option>
                  </Select>
                </FormField>
                <FormField label="Status">
                  <Select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value as any })}>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                  </Select>
                </FormField>
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
              <Button loading={busy} onClick={submitEdit}>Save changes</Button>
            </div>
          </>
        )}
      </Modal>

      <ConfirmDialog
        open={!!del}
        title="Remove teammate?"
        message={`Remove ${del?.firstName} ${del?.lastName}? They'll lose access immediately.`}
        danger
        onConfirm={async () => { await settingsService.removeTeamUser(del!.userUid); toast.success('Removed'); setDel(null); refresh(); }}
        onCancel={() => setDel(null)}
      />
    </SettingsPage>
  );
}
